import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as customerManagementActions from "_redux/actions/customerManagement";

export const useCustomerManagementStore = () => {
  const dispatch = useDispatch();

  const _customerManagement = useSelector((store) => store.customerManagement);

  const signup = useCallback(
    (user) => dispatch(customerManagementActions.signup(user)),
    [dispatch]
  );

  const clearSignupStatus = useCallback(
    () => dispatch(customerManagementActions.clearSignupStatus()),
    [dispatch]
  );

  const signIn = useCallback(
    (user, options) => dispatch(customerManagementActions.signIn(user, options)),
    [dispatch]
  );

  const clearSignInStatus = useCallback(
    () => dispatch(customerManagementActions.clearSignInStatus()),
    [dispatch]
  );

  const changePassword = useCallback(
    (changePasswordData) =>
      dispatch(customerManagementActions.changePassword(changePasswordData)),
    [dispatch]
  );

  const clearChangePasswordStatus = useCallback(
    () => dispatch(customerManagementActions.clearChangePasswordStatus()),
    [dispatch]
  );

  const refreshToken = useCallback(
    (refreshTokenData) =>
      dispatch(customerManagementActions.refreshToken(refreshTokenData)),
    [dispatch]
  );

  const logout = useCallback(
    () => dispatch(customerManagementActions.logout()),
    [dispatch]
  );

  const submitDocuments = useCallback(
    (proofOfIdentityDocuments) =>
      dispatch(
        customerManagementActions.submitDocuments(proofOfIdentityDocuments)
      ),
    [dispatch]
  );

  const clearSubmitDocumentsStatus = useCallback(
    () => dispatch(customerManagementActions.clearSubmitDocumentsStatus()),
    [dispatch]
  );

  const submitEmploymentDetails = useCallback(
    (employmentDetails) =>
      dispatch(
        customerManagementActions.submitEmploymentDetails(employmentDetails)
      ),
    [dispatch]
  );

  const clearSubmitEmploymentDetailsStatus = useCallback(
    () =>
      dispatch(customerManagementActions.clearSubmitEmploymentDetailsStatus()),
    [dispatch]
  );

  const updateEmploymentDetails = useCallback(
    (employmentDetails) =>
      dispatch(
        customerManagementActions.updateEmploymentDetails(employmentDetails)
      ),
    [dispatch]
  );

  const clearUpdateEmploymentDetailsStatus = useCallback(
    () =>
      dispatch(customerManagementActions.clearUpdateEmploymentDetailsStatus()),
    [dispatch]
  );

  const fetchEmploymentDetails = useCallback(
    () => dispatch(customerManagementActions.fetchEmploymentDetails()),
    [dispatch]
  );

  const clearEmploymentDetailsStatus = useCallback(
    () => dispatch(customerManagementActions.clearEmploymentDetailsStatus()),
    [dispatch]
  );

  const fetchFinancialDetails = useCallback(
    () => dispatch(customerManagementActions.fetchFinancialDetails()),
    [dispatch]
  );

  const clearFinancialDetailsStatus = useCallback(
    () => dispatch(customerManagementActions.clearFinancialDetailsStatus()),
    [dispatch]
  );

  const submitFinancialDetails = useCallback(
    (financialDetails) =>
      dispatch(
        customerManagementActions.submitFinancialDetails(financialDetails)
      ),
    [dispatch]
  );

  const clearSubmitFinancialDetailsStatus = useCallback(
    () =>
      dispatch(customerManagementActions.clearSubmitFinancialDetailsStatus()),
    [dispatch]
  );

  const updateFinancialDetails = useCallback(
    (financialDetails) =>
      dispatch(
        customerManagementActions.updateFinancialDetails(financialDetails)
      ),
    [dispatch]
  );

  const clearUpdateFinancialDetailsStatus = useCallback(
    () =>
      dispatch(customerManagementActions.clearUpdateFinancialDetailsStatus()),
    [dispatch]
  );

  const fetchPersonalDetails = useCallback(
    () => dispatch(customerManagementActions.fetchPersonalDetails()),
    [dispatch]
  );

  const clearPersonalDetailsStatus = useCallback(
    () => dispatch(customerManagementActions.clearPersonalDetailsStatus()),
    [dispatch]
  );

  const submitPersonalDetails = useCallback(
    (personalDetails) =>
      dispatch(
        customerManagementActions.submitPersonalDetails(personalDetails)
      ),
    [dispatch]
  );

  const clearSubmitPersonalDetailsStatus = useCallback(
    () =>
      dispatch(customerManagementActions.clearSubmitPersonalDetailsStatus()),
    [dispatch]
  );

  const updatePersonalDetails = useCallback(
    (personalDetails) =>
      dispatch(
        customerManagementActions.updatePersonalDetails(personalDetails)
      ),
    [dispatch]
  );

  const clearUpdatePersonalDetailsStatus = useCallback(
    () =>
      dispatch(customerManagementActions.clearUpdatePersonalDetailsStatus()),
    [dispatch]
  );

  const fetchCountries = useCallback(
    () => dispatch(customerManagementActions.fetchCountries()),
    [dispatch]
  );

  const fetchIndustries = useCallback(
    () => dispatch(customerManagementActions.fetchIndustries()),
    [dispatch]
  );

  const fetchOnboardingStatus = useCallback(
    () => dispatch(customerManagementActions.fetchOnboardingStatus()),
    [dispatch]
  );

  const clearOnboardingStatus = useCallback(
    () => dispatch(customerManagementActions.clearOnboardingStatus()),
    [dispatch]
  );

  const onboardingValidate = useCallback(
    (onboardingDetails) =>
      dispatch(
        customerManagementActions.onboardingValidate(onboardingDetails)
      ),
    [dispatch]
  );

  const clearOnboardingValidateStatus = useCallback(
    () =>
      dispatch(customerManagementActions.clearOnboardingValidateStatus()),
    [dispatch]
  );

  const onboardingRegister = useCallback(
    (registerDetails) =>
      dispatch(
        customerManagementActions.onboardingRegister(registerDetails)
      ),
    [dispatch]
  );

  const clearOnboardingRegisterStatus = useCallback(
    () =>
      dispatch(customerManagementActions.clearOnboardingRegisterStatus()),
    [dispatch]
  );

  const fetchTradingExperience = useCallback(
    () => dispatch(customerManagementActions.fetchTradingExperience()),
    [dispatch]
  );

  const clearTradingExperienceStatus = useCallback(
    () =>
      dispatch(customerManagementActions.clearTradingExperienceStatus()),
    [dispatch]
  );

  const submitTradingExperience = useCallback(
    (tradingExperienceDetails) =>
      dispatch(
        customerManagementActions.submitTradingExperience(
          tradingExperienceDetails
        )
      ),
    [dispatch]
  );

  const clearSubmitTradingExperienceStatus = useCallback(
    () =>
      dispatch(customerManagementActions.clearSubmitTradingExperienceStatus()),
    [dispatch]
  );

  const updateTradingExperience = useCallback(
    (tradingExperienceDetails) =>
      dispatch(
        customerManagementActions.updateTradingExperience(
          tradingExperienceDetails
        )
      ),
    [dispatch]
  );

  const clearUpdateTradingExperienceStatus = useCallback(
    () =>
      dispatch(customerManagementActions.clearUpdateTradingExperienceStatus()),
    [dispatch]
  );

  const fetchTradingAccounts = useCallback(
    () => dispatch(customerManagementActions.fetchTradingAccounts()),
    [dispatch]
  );

  const clearTradingAccountsStatus = useCallback(
    () => dispatch(customerManagementActions.clearTradingAccountsStatus()),
    [dispatch]
  );

  const submitTradingAccounts = useCallback(
    (tradingAccount, scope) =>
      dispatch(
        customerManagementActions.submitTradingAccounts(tradingAccount, scope)
      ),
    [dispatch]
  );

  const clearSubmitTradingAccountsStatus = useCallback(
    () =>
      dispatch(customerManagementActions.clearSubmitTradingAccountsStatus()),
    [dispatch]
  );

  const sendOTP = useCallback(
    (sendOTPDetails) =>
      dispatch(customerManagementActions.sendOTP(sendOTPDetails)),
    [dispatch]
  );

  const clearSendOTPStatus = useCallback(
    () => dispatch(customerManagementActions.clearSendOTPStatus()),
    [dispatch]
  );

  const verifyOTP = useCallback(
    (verifyOTPDetails) =>
      dispatch(customerManagementActions.verifyOTP(verifyOTPDetails)),
    [dispatch]
  );

  const clearVerifyOTPStatus = useCallback(
    () => dispatch(customerManagementActions.clearVerifyOTPStatus()),
    [dispatch]
  );

  const fetchUser = useCallback(
    () => dispatch(customerManagementActions.fetchUser()),
    [dispatch]
  );

  const clearUserStatus = useCallback(
    () => dispatch(customerManagementActions.clearUserStatus()),
    [dispatch]
  );

  const socialSignIn = useCallback(
    (socialSignInDetails) =>
      dispatch(customerManagementActions.socialSignIn(socialSignInDetails)),
    [dispatch]
  );

  const clearSocialSignInStatus = useCallback(
    () => dispatch(customerManagementActions.clearSocialSignInStatus()),
    [dispatch]
  );

  const socialSignup = useCallback(
    (socialSignupDetails) =>
      dispatch(customerManagementActions.socialSignup(socialSignupDetails)),
    [dispatch]
  );

  const clearSocialSignupStatus = useCallback(
    () => dispatch(customerManagementActions.clearSocialSignupStatus()),
    [dispatch]
  );

  const setSignupDetails = useCallback(
    (signUpDetails) =>
      dispatch(customerManagementActions.setSignupDetails(signUpDetails)),
    [dispatch]
  );

  const emailVerification = useCallback(
    (emailVerificationDetails) =>
      dispatch(
        customerManagementActions.emailVerification(emailVerificationDetails)
      ),
    [dispatch]
  );

  const clearEmailVerificationStatus = useCallback(
    () => dispatch(customerManagementActions.clearEmailVerificationStatus()),
    [dispatch]
  );

  const phoneVerification = useCallback(
    (emailVerificationDetails) =>
      dispatch(
        customerManagementActions.phoneVerification(emailVerificationDetails)
      ),
    [dispatch]
  );

  const clearPhoneVerificationStatus = useCallback(
    () => dispatch(customerManagementActions.clearPhoneVerificationStatus()),
    [dispatch]
  );

  const fetchAccountTypes = useCallback(
    (params) => dispatch(customerManagementActions.fetchAccountTypes(params)),
    [dispatch]
  );

  const clearAccountTypesStatus = useCallback(
    () => dispatch(customerManagementActions.clearAccountTypesStatus()),
    [dispatch]
  );

  const fetchPlatformServers = useCallback(
    () => dispatch(customerManagementActions.fetchPlatformServers()),
    [dispatch]
  );

  const clearPlatformServersStatus = useCallback(
    () => dispatch(customerManagementActions.clearPlatformServersStatus()),
    [dispatch]
  );

  const initiateForgotPassword = useCallback(
    (initiateForgotPasswordDetails) =>
      dispatch(
        customerManagementActions.initiateForgotPassword(
          initiateForgotPasswordDetails
        )
      ),
    [dispatch]
  );

  const clearInitiateForgotPasswordStatus = useCallback(
    () =>
      dispatch(customerManagementActions.clearInitiateForgotPasswordStatus()),
    [dispatch]
  );

  const forgotPasswordReset = useCallback(
    (forgotPasswordResetDetails) =>
      dispatch(
        customerManagementActions.forgotPasswordReset(
          forgotPasswordResetDetails
        )
      ),
    [dispatch]
  );

  const clearForgotPasswordResetStatus = useCallback(
    () => dispatch(customerManagementActions.clearForgotPasswordResetStatus()),
    [dispatch]
  );

  const updateLeverage = useCallback(
    (updateLeverageDetails) =>
      dispatch(customerManagementActions.updateLeverage(updateLeverageDetails)),
    [dispatch]
  );

  const clearUpdateLeverageStatus = useCallback(
    () => dispatch(customerManagementActions.clearUpdateLeverageStatus()),
    [dispatch]
  );

  const initiateResetPassword = useCallback(
    (initiateResetPasswordDetails) =>
      dispatch(
        customerManagementActions.initiateResetPassword(
          initiateResetPasswordDetails
        )
      ),
    [dispatch]
  );

  const clearInitiateResetPasswordStatus = useCallback(
    () =>
      dispatch(customerManagementActions.clearInitiateResetPasswordStatus()),
    [dispatch]
  );

  const resetPassword = useCallback(
    (resetPasswordDetails) =>
      dispatch(customerManagementActions.resetPassword(resetPasswordDetails)),
    [dispatch]
  );

  const clearResetPasswordStatus = useCallback(
    () => dispatch(customerManagementActions.clearResetPasswordStatus()),
    [dispatch]
  );

  const fetchIntroducingBrokerApplication = useCallback(
    () =>
      dispatch(customerManagementActions.fetchIntroducingBrokerApplication()),
    [dispatch]
  );

  const fetchSocialTradingProviders = useCallback(
    () => dispatch(customerManagementActions.fetchSocialTradingProviders()),
    [dispatch]
  );

  const clearSocialTradingProvidersStatus = useCallback(
    () =>
      dispatch(customerManagementActions.clearSocialTradingProvidersStatus()),
    [dispatch]
  );

  const fetchSocialTradingProviderOffers = useCallback(
    (socialTradingProviderDetails) =>
      dispatch(
        customerManagementActions.fetchSocialTradingProviderOffers(
          socialTradingProviderDetails
        )
      ),
    [dispatch]
  );

  const clearSocialTradingProviderOffersStatus = useCallback(
    () =>
      dispatch(
        customerManagementActions.clearSocialTradingProviderOffersStatus()
      ),
    [dispatch]
  );

  const subscribeToSocialTradingProvider = useCallback(
    (socialTradingProviderSubscriptionDetails) =>
      dispatch(
        customerManagementActions.subscribeToSocialTradingProvider(
          socialTradingProviderSubscriptionDetails
        )
      ),
    [dispatch]
  );

  const clearSubscribeToSocialTradingProviderStatus = useCallback(
    () =>
      dispatch(
        customerManagementActions.clearSubscribeToSocialTradingProviderStatus()
      ),
    [dispatch]
  );

  const transfers = useCallback(
    (transfersDetails) =>
      dispatch(customerManagementActions.transfers(transfersDetails)),
    [dispatch]
  );

  const clearTransfersStatus = useCallback(
    () => dispatch(customerManagementActions.clearTransfersStatus()),
    [dispatch]
  );

  const clearIntroducingBrokerApplicationStatus = useCallback(
    () =>
      dispatch(
        customerManagementActions.clearIntroducingBrokerApplicationStatus()
      ),
    [dispatch]
  );

  const submitIntroducingBrokerApplication = useCallback(
    (introducingBrokerApplicationDetails) =>
      dispatch(
        customerManagementActions.submitIntroducingBrokerApplication(
          introducingBrokerApplicationDetails
        )
      ),
    [dispatch]
  );

  const clearSubmitIntroducingBrokerApplicationStatus = useCallback(
    () =>
      dispatch(
        customerManagementActions.clearSubmitIntroducingBrokerApplicationStatus()
      ),
    [dispatch]
  );

  const fetchReferralLinks = useCallback(
    () => dispatch(customerManagementActions.fetchReferralLinks()),
    [dispatch]
  );

  const clearReferralLinksStatus = useCallback(
    () => dispatch(customerManagementActions.clearReferralLinksStatus()),
    [dispatch]
  );

  const submitReferralLink = useCallback(
    (referralLinkDetails) =>
      dispatch(
        customerManagementActions.submitReferralLink(referralLinkDetails)
      ),
    [dispatch]
  );

  const clearSubmitReferralLinkStatus = useCallback(
    () => dispatch(customerManagementActions.clearSubmitReferralLinkStatus()),
    [dispatch]
  );

  const fetchNews = useCallback(
    (newsParams) => dispatch(customerManagementActions.fetchNews(newsParams)),
    [dispatch]
  );

  const clearNewsStatus = useCallback(
    () => dispatch(customerManagementActions.clearNewsStatus()),
    [dispatch]
  );

  const fetchClientCabinetAccountCommission = useCallback(
    (accountCommissionParams) =>
      dispatch(
        customerManagementActions.fetchClientCabinetAccountCommission(
          accountCommissionParams
        )
      ),
    [dispatch]
  );

  const clearClientCabinetAccountCommissionStatus = useCallback(
    () =>
      dispatch(
        customerManagementActions.clearClientCabinetAccountCommissionStatus()
      ),
    [dispatch]
  );
  const fetchClientCabinetClientCommission = useCallback(
    (clientCommissionParams) =>
      dispatch(
        customerManagementActions.fetchClientCabinetClientCommission(
          clientCommissionParams
        )
      ),
    [dispatch]
  );

  const clearClientCabinetClientCommissionStatus = useCallback(
    () =>
      dispatch(
        customerManagementActions.clearClientCabinetClientCommissionStatus()
      ),
    [dispatch]
  );

  const fetchClientCabinetCommissionSettings = useCallback(
    (commissionSettingParams) =>
      dispatch(
        customerManagementActions.fetchClientCabinetCommissionSettings(
          commissionSettingParams
        )
      ),
    [dispatch]
  );

  const clearClientCabinetCommissionSettingsStatus = useCallback(
    () =>
      dispatch(
        customerManagementActions.clearClientCabinetCommissionSettingsStatus()
      ),
    [dispatch]
  );

  const fetchClientCabinetIBCommissions = useCallback(
    (ibCommissionsParams) =>
      dispatch(
        customerManagementActions.fetchClientCabinetIBCommissions(
          ibCommissionsParams
        )
      ),
    [dispatch]
  );

  const clearClientCabinetIBCommissionsStatus = useCallback(
    () =>
      dispatch(
        customerManagementActions.clearClientCabinetIBCommissionsStatus()
      ),
    [dispatch]
  );

  const fetchClientCabinetIBPerformanceDashboard = useCallback(
    (ibPerformanceDashboardParams) =>
      dispatch(
        customerManagementActions.fetchClientCabinetIBPerformanceDashboard(
          ibPerformanceDashboardParams
        )
      ),
    [dispatch]
  );

  const clearClientCabinetIBPerformanceDashboardStatus = useCallback(
    () =>
      dispatch(
        customerManagementActions.clearClientCabinetIBPerformanceDashboardStatus()
      ),
    [dispatch]
  );

  const fetchClientCabinetIBReferrals = useCallback(
    (ibReferralsParams) =>
      dispatch(
        customerManagementActions.fetchClientCabinetIBReferrals(
          ibReferralsParams
        )
      ),
    [dispatch]
  );

  const clearClientCabinetIBReferralsStatus = useCallback(
    () =>
      dispatch(customerManagementActions.clearClientCabinetIBReferralsStatus()),
    [dispatch]
  );

  const fetchClientCabinetIBTransactions = useCallback(
    (ibTransactionsParams) =>
      dispatch(
        customerManagementActions.fetchClientCabinetIBTransactions(
          ibTransactionsParams
        )
      ),
    [dispatch]
  );

  const clearClientCabinetIBTransactionsStatus = useCallback(
    () =>
      dispatch(
        customerManagementActions.clearClientCabinetIBTransactionsStatus()
      ),
    [dispatch]
  );

  const monitorClients = useCallback(
    (monitorClientsParams) =>
      dispatch(customerManagementActions.monitorClients(monitorClientsParams)),
    [dispatch]
  );

  const clearMonitorClientsStatus = useCallback(
    () => dispatch(customerManagementActions.clearMonitorClientsStatus()),
    [dispatch]
  );

  const monitorMetaClients = useCallback(
    (monitorMetaClientsParams) =>
      dispatch(
        customerManagementActions.monitorMetaClients(monitorMetaClientsParams)
      ),
    [dispatch]
  );

  const clearMonitorMetaClientsStatus = useCallback(
    () => dispatch(customerManagementActions.clearMonitorMetaClientsStatus()),
    [dispatch]
  );

  const submitSocialTradingProvider = useCallback(
    (submitSocialTradingProviderDetails) =>
      dispatch(
        customerManagementActions.submitSocialTradingProvider(
          submitSocialTradingProviderDetails
        )
      ),
    [dispatch]
  );

  const clearSubmitSocialTradingProviderStatus = useCallback(
    () =>
      dispatch(
        customerManagementActions.clearSubmitSocialTradingProviderStatus()
      ),
    [dispatch]
  );

  const submitSocialTradingProviderOffer = useCallback(
    (submitSocialTradingProviderOfferDetails) =>
      dispatch(
        customerManagementActions.submitSocialTradingProviderOffer(
          submitSocialTradingProviderOfferDetails
        )
      ),
    [dispatch]
  );

  const clearSubmitSocialTradingProviderOfferStatus = useCallback(
    () =>
      dispatch(
        customerManagementActions.clearSubmitSocialTradingProviderOfferStatus()
      ),
    [dispatch]
  );

  const fetchSocialTradingProvidersConfig = useCallback(
    () =>
      dispatch(customerManagementActions.fetchSocialTradingProvidersConfig()),
    [dispatch]
  );

  const clearSocialTradingProviderConfigStatus = useCallback(
    () =>
      dispatch(
        customerManagementActions.clearSocialTradingProviderConfigStatus()
      ),
    [dispatch]
  );

  const fetchSocialTradingProviderPositions = useCallback(
    (socialTradingProviderPositionsDetails) =>
      dispatch(
        customerManagementActions.fetchSocialTradingProviderPositions(
          socialTradingProviderPositionsDetails
        )
      ),
    [dispatch]
  );

  const clearSocialTradingProviderPositions = useCallback(
    () =>
      dispatch(customerManagementActions.clearSocialTradingProviderPositions()),
    [dispatch]
  );

  const fetchSocialTradingProviderDetails = useCallback(
    (socialTradingProviderDetails) =>
      dispatch(
        customerManagementActions.fetchSocialTradingProviderDetails(
          socialTradingProviderDetails
        )
      ),
    [dispatch]
  );

  const clearSocialTradingProviderDetailsStatus = useCallback(
    () =>
      dispatch(
        customerManagementActions.clearSocialTradingProviderDetailsStatus()
      ),
    [dispatch]
  );

  const fetchSocialTradingHistoryDailyChart = useCallback(
    (socialTradingHistoryChartDetails) =>
      dispatch(
        customerManagementActions.fetchSocialTradingHistoryDailyChart(
          socialTradingHistoryChartDetails
        )
      ),
    [dispatch]
  );

  const clearSocialTradingHistoryChartStatus = useCallback(
    () =>
      dispatch(
        customerManagementActions.clearSocialTradingHistoryChartStatus()
      ),
    [dispatch]
  );

  /**
   * @param {Object} socialTradingProviderMetricsParams
   * @param {Integer} socialTradingProviderMetricsParams.providerId
   */

  const fetchSocialTradingProviderMetrics = useCallback(
    (socialTradingProviderMetricsParams) =>
      dispatch(
        customerManagementActions.fetchSocialTradingProviderMetrics(
          socialTradingProviderMetricsParams
        )
      ),
    [dispatch]
  );

  const clearSocialTradingProviderMetricsStatus = useCallback(
    () =>
      dispatch(
        customerManagementActions.clearSocialTradingProviderMetricsStatus()
      ),
    [dispatch]
  );

  /**
   * @param {Object} socialTradingProviderSubscriptionsParams
   * @param {Integer} socialTradingProviderSubscriptionsParams.providerId
   * @param {Integer} socialTradingProviderSubscriptionsParams.page
   * @param {Integer} socialTradingProviderSubscriptionsParams.pageSize
   */

  const fetchSocialTradingProviderSubscriptions = useCallback(
    (socialTradingProviderSubscriptionsParams) =>
      dispatch(
        customerManagementActions.fetchSocialTradingProviderSubscriptions(
          socialTradingProviderSubscriptionsParams
        )
      ),
    [dispatch]
  );

  const clearSocialTradingProviderSubscriptionsStatus = useCallback(
    () =>
      dispatch(
        customerManagementActions.clearSocialTradingProviderSubscriptionsStatus()
      ),
    [dispatch]
  );

  /**
   * @param {Object} socialTradingProviderDealsParams
   * @param {Integer} socialTradingProviderDealsParams.providerId
   * @param {Integer} socialTradingProviderDealsParams.page
   * @param {Integer} socialTradingProviderDealsParams.pageSize
   */

  const fetchSocialTradingProviderDeals = useCallback(
    (socialTradingProviderDealsParams) =>
      dispatch(
        customerManagementActions.fetchSocialTradingProviderDeals(
          socialTradingProviderDealsParams
        )
      ),
    [dispatch]
  );

  const clearSocialTradingProviderDealsStatus = useCallback(
    () =>
      dispatch(
        customerManagementActions.clearSocialTradingProviderDealsStatus()
      ),
    [dispatch]
  );

  /**
@param {Object} updateSocialTradingProviderDetails
@param {Integer} updateSocialTradingProviderDetails.providerId
@param {String} updateSocialTradingProviderDetails.nickname
@param {Object} updateSocialTradingProviderDetails.additional
@param {Object} updateSocialTradingProviderDetails.publicData
@param {Object} updateSocialTradingProviderDetails.strategy
@param {String} updateSocialTradingProviderDetails.visibility

@returns {Promise}
*/

  const updateSocialTradingProvider = useCallback(
    (updateSocialTradingProviderDetails) =>
      dispatch(
        customerManagementActions.updateSocialTradingProvider(
          updateSocialTradingProviderDetails
        )
      ),
    [dispatch]
  );

  const clearUpdateSocialTradingProviderStatus = useCallback(
    () =>
      dispatch(
        customerManagementActions.clearUpdateSocialTradingProviderStatus()
      ),
    [dispatch]
  );

  const fetchSocialTradingProviderPortfolio = useCallback(
    () =>
      dispatch(customerManagementActions.fetchSocialTradingProviderPortfolio()),
    [dispatch]
  );

  const clearSocialTradingProviderPortfolioStatus = useCallback(
    () =>
      dispatch(
        customerManagementActions.clearSocialTradingProviderPortfolioStatus()
      ),
    [dispatch]
  );

  /**
   * @param {Object} updateSocialTradingSubscriptionDetails
   * @param {Integer} updateSocialTradingSubscriptionDetails.subscriptionId
   * @param {Object} updateSocialTradingSubscriptionDetails.correction
   * @param {Object} updateSocialTradingSubscriptionDetails.filters
   * @param {Object} updateSocialTradingSubscriptionDetails.strategy
   */

  const updateSocialTradingSubscription = useCallback(
    (updateSocialTradingSubscriptionDetails) =>
      dispatch(
        customerManagementActions.updateSocialTradingSubscription(
          updateSocialTradingSubscriptionDetails
        )
      ),
    [dispatch]
  );

  const clearUpdateSocialTradingSubscriptionStatus = useCallback(
    () =>
      dispatch(
        customerManagementActions.clearUpdateSocialTradingSubscriptionStatus()
      ),
    [dispatch]
  );

  /**
   * @param {Object} socialTradingSubscriptionParams
   * @param {Integer} socialTradingSubscriptionParams.subscriptionId
   */

  const fetchSocialTradingSubscriptionDetails = useCallback(
    (socialTradingSubscriptionParams) =>
      dispatch(
        customerManagementActions.fetchSocialTradingSubscriptionDetails(
          socialTradingSubscriptionParams
        )
      ),
    [dispatch]
  );

  const clearSocialTradingSubscriptionDetailsStatus = useCallback(
    () =>
      dispatch(
        customerManagementActions.clearSocialTradingSubscriptionDetailsStatus()
      ),
    [dispatch]
  );

  /**
   * @param {Object} socialTradingSubscriptionDealsParams
   * @param {Integer} socialTradingSubscriptionDealsParams.subscriptionId
   * @param {Integer} socialTradingSubscriptionDealsParams.page
   * @param {Integer} socialTradingSubscriptionDealsParams.pageSize
   */

  const fetchSocialTradingSubscriptionDeals = useCallback(
    (socialTradingSubscriptionDealsParams) =>
      dispatch(
        customerManagementActions.fetchSocialTradingSubscriptionDeals(
          socialTradingSubscriptionDealsParams
        )
      ),
    [dispatch]
  );

  const clearSocialTradingSubscriptionDealsStatus = useCallback(
    () =>
      dispatch(
        customerManagementActions.clearSocialTradingSubscriptionDealsStatus()
      ),
    [dispatch]
  );

  /**
   * @param {Object} socialTradingSubscriptionMetricsParams
   * @param {Integer} socialTradingSubscriptionMetricsParams.subscriptionId
   */

  const fetchSocialTradingSubscriptionMetrics = useCallback(
    (socialTradingSubscriptionMetricsParams) =>
      dispatch(
        customerManagementActions.fetchSocialTradingSubscriptionMetrics(
          socialTradingSubscriptionMetricsParams
        )
      ),
    [dispatch]
  );

  const clearSocialTradingSubscriptionMetricsStatus = useCallback(
    () =>
      dispatch(
        customerManagementActions.clearSocialTradingSubscriptionMetricsStatus()
      ),
    [dispatch]
  );

  /**
   * @param {Object} socialTradingSubscriptionTradingResultsParams
   * @param {Integer} socialTradingSubscriptionTradingResultsParams.subscriptionId
   * @param {Integer} socialTradingSubscriptionTradingResultsParams.page
   * @param {Integer} socialTradingSubscriptionTradingResultsParams.pageSize
   */

  const fetchSocialTradingSubscriptionTradingResults = useCallback(
    (socialTradingSubscriptionTradingResultsParams) =>
      dispatch(
        customerManagementActions.fetchSocialTradingSubscriptionTradingResults(
          socialTradingSubscriptionTradingResultsParams
        )
      ),
    [dispatch]
  );

  const clearSocialTradingSubscriptionTradingResultsStatus = useCallback(
    () =>
      dispatch(
        customerManagementActions.clearSocialTradingSubscriptionTradingResultsStatus()
      ),
    [dispatch]
  );

  /**
   * @param {Object} socialTradingSubscriptionRiskParams
   * @param {Integer} socialTradingSubscriptionRiskParams.subscriptionId
   */

  const fetchSocialTradingSubscriptionRisk = useCallback(
    (socialTradingSubscriptionRiskParams) =>
      dispatch(
        customerManagementActions.fetchSocialTradingSubscriptionRisk(
          socialTradingSubscriptionRiskParams
        )
      ),
    [dispatch]
  );

  const clearSocialTradingSubscriptionRiskStatus = useCallback(
    () =>
      dispatch(
        customerManagementActions.clearSocialTradingSubscriptionRiskStatus()
      ),
    [dispatch]
  );

  /**
   * @param {Object} updateSocialTradingSubscriptionRiskDetails
   * @param {Integer} updateSocialTradingSubscriptionRiskDetails.subscriptionId
   * @param {Integer} updateSocialTradingSubscriptionRiskDetails.rules
   */

  const updateSocialTradingSubscriptionRisk = useCallback(
    (updateSocialTradingSubscriptionRiskDetails) =>
      dispatch(
        customerManagementActions.updateSocialTradingSubscriptionRisk(
          updateSocialTradingSubscriptionRiskDetails
        )
      ),
    [dispatch]
  );

  const clearUpdateSocialTradingSubscriptionRiskStatus = useCallback(
    () =>
      dispatch(
        customerManagementActions.clearUpdateSocialTradingSubscriptionRiskStatus()
      ),
    [dispatch]
  );

  /**
   * @param {Object} socialTradingSubscriptionDetails
   * @param {Integer} socialTradingSubscriptionDetails.subscriptionId
   * @param {Integer} socialTradingSubscriptionDetails.keepPositions
   */

  const deleteSocialTradingSubscription = useCallback(
    (socialTradingSubscriptionDetails) =>
      dispatch(
        customerManagementActions.deleteSocialTradingSubscription(
          socialTradingSubscriptionDetails
        )
      ),
    [dispatch]
  );

  const clearDeleteSocialTradingSubscriptionStatus = useCallback(
    () =>
      dispatch(
        customerManagementActions.clearDeleteSocialTradingSubscriptionStatus()
      ),
    [dispatch]
  );

  const fetchSocialTradingFollowerPortfolio = useCallback(
    () =>
      dispatch(customerManagementActions.fetchSocialTradingFollowerPortfolio()),
    [dispatch]
  );

  const clearSocialTradingFollowerPortfolioStatus = useCallback(
    () =>
      dispatch(
        customerManagementActions.clearSocialTradingFollowerPortfolioStatus()
      ),
    [dispatch]
  );

  /**
   * @param {Object} socialTradingFollowerPositionsParams
   * @param {Integer} socialTradingFollowerPositionsParams.subscriptionId
   */

  const fetchSocialTradingFollowerPositions = useCallback(
    (socialTradingFollowerPositionsParams) =>
      dispatch(
        customerManagementActions.fetchSocialTradingFollowerPositions(
          socialTradingFollowerPositionsParams
        )
      ),
    [dispatch]
  );

  const clearSocialTradingFollowerPositionsStatus = useCallback(
    () =>
      dispatch(
        customerManagementActions.clearSocialTradingFollowerPositionsStatus()
      ),
    [dispatch]
  );

  const fetchSocialTradingOfferDetails = useCallback(
    (socialTradingOfferDetailsParams) =>
      dispatch(
        customerManagementActions.fetchSocialTradingOfferDetails(
          socialTradingOfferDetailsParams
        )
      ),
    [dispatch]
  );

  const clearSocialTradingOfferDetailsStatus = useCallback(
    () =>
      dispatch(
        customerManagementActions.clearSocialTradingOfferDetailsStatus()
      ),
    [dispatch]
  );

  const fetchSocialTradingFees = useCallback(
    (socialTradingFeesParams) =>
      dispatch(
        customerManagementActions.fetchSocialTradingFees(
          socialTradingFeesParams
        )
      ),
    [dispatch]
  );

  const clearSocialTradingFeesStatus = useCallback(
    () => dispatch(customerManagementActions.clearSocialTradingFeesStatus()),
    [dispatch]
  );

  const fetchContacts = useCallback(
    () => dispatch(customerManagementActions.fetchContacts()),
    [dispatch]
  );

  const clearContactsStatus = useCallback(
    () => dispatch(customerManagementActions.clearContactsStatus()),
    [dispatch]
  );

  const submitContact = useCallback(
    (submitContactDetails) =>
      dispatch(customerManagementActions.submitContact(submitContactDetails)),
    [dispatch]
  );

  const clearSubmitContactStatus = useCallback(
    () => dispatch(customerManagementActions.clearSubmitContactStatus()),
    [dispatch]
  );

  const fetchSwapFreeDetails = useCallback(
    (params) =>
      dispatch(customerManagementActions.fetchSwapFreeDetails(params)),
    [dispatch]
  );

  const clearSwapFreeDetailsStatus = useCallback(
    () => dispatch(customerManagementActions.clearSwapFreeDetailsStatus()),
    [dispatch]
  );

  const submitSwapFree = useCallback(
    (submitSwapFreeDetails) =>
      dispatch(customerManagementActions.submitSwapFree(submitSwapFreeDetails)),
    [dispatch]
  );

  const clearSubmitSwapFreeStatus = useCallback(
    () => dispatch(customerManagementActions.clearSubmitSwapFreeStatus()),
    [dispatch]
  );

  const hydrateUserFromToken = useCallback(
    () => dispatch(customerManagementActions.hydrateUserFromToken()),
    [dispatch]
  );

  const submitProviderApplication = useCallback(
    (applicationDetails) =>
      dispatch(
        customerManagementActions.submitProviderApplication(applicationDetails)
      ),
    [dispatch]
  );

  const clearSubmitProviderApplicationStatus = useCallback(
    () =>
      dispatch(
        customerManagementActions.clearSubmitProviderApplicationStatus()
      ),
    [dispatch]
  );

  const fetchSocialTradingRateProfile = useCallback(
    (socialTradingRateProfileParams) =>
      dispatch(
        customerManagementActions.fetchSocialTradingRateProfile(
          socialTradingRateProfileParams
        )
      ),
    [dispatch]
  );

  const clearSocialTradingRateProfileStatus = useCallback(
    () =>
      dispatch(
        customerManagementActions.clearSocialTradingRateProfileStatus()
      ),
    [dispatch]
  );

  const fetchSocialTradingHistoryIndicators = useCallback(
    (socialTradingHistoryIndicatorsParams) =>
      dispatch(
        customerManagementActions.fetchSocialTradingHistoryIndicators(
          socialTradingHistoryIndicatorsParams
        )
      ),
    [dispatch]
  );

  const clearSocialTradingHistoryIndicatorsStatus = useCallback(
    () =>
      dispatch(
        customerManagementActions.clearSocialTradingHistoryIndicatorsStatus()
      ),
    [dispatch]
  );

  const fetchSocialTradingInstrumentReport = useCallback(
    (socialTradingInstrumentReportParams) =>
      dispatch(
        customerManagementActions.fetchSocialTradingInstrumentReport(
          socialTradingInstrumentReportParams
        )
      ),
    [dispatch]
  );

  const clearSocialTradingInstrumentReportStatus = useCallback(
    () =>
      dispatch(
        customerManagementActions.clearSocialTradingInstrumentReportStatus()
      ),
    [dispatch]
  );

  const fetchSocialTradingTradeReport = useCallback(
    (socialTradingTradeReportParams) =>
      dispatch(
        customerManagementActions.fetchSocialTradingTradeReport(
          socialTradingTradeReportParams
        )
      ),
    [dispatch]
  );

  const clearSocialTradingTradeReportStatus = useCallback(
    () =>
      dispatch(
        customerManagementActions.clearSocialTradingTradeReportStatus()
      ),
    [dispatch]
  );

  return {
    customerManagement: _customerManagement,
    signIn,
    clearSignupStatus,
    refreshToken,
    logout,
    submitDocuments,
    submitEmploymentDetails,
    clearSubmitDocumentsStatus,
    clearSubmitEmploymentDetailsStatus,
    updateEmploymentDetails,
    clearUpdateEmploymentDetailsStatus,
    fetchEmploymentDetails,
    clearEmploymentDetailsStatus,
    fetchFinancialDetails,
    clearFinancialDetailsStatus,
    submitFinancialDetails,
    clearSubmitFinancialDetailsStatus,
    updateFinancialDetails,
    clearUpdateFinancialDetailsStatus,
    signup,
    changePassword,
    fetchPersonalDetails,
    submitPersonalDetails,
    clearSubmitPersonalDetailsStatus,
    updatePersonalDetails,
    clearUpdatePersonalDetailsStatus,
    fetchCountries,
    fetchIndustries,
    clearSignInStatus,
    clearPersonalDetailsStatus,
    clearChangePasswordStatus,
    fetchOnboardingStatus,
    clearOnboardingStatus,
    onboardingValidate,
    clearOnboardingValidateStatus,
    onboardingRegister,
    clearOnboardingRegisterStatus,
    fetchTradingExperience,
    clearTradingExperienceStatus,
    submitTradingExperience,
    clearSubmitTradingExperienceStatus,
    updateTradingExperience,
    clearUpdateTradingExperienceStatus,
    fetchTradingAccounts,
    clearTradingAccountsStatus,
    submitTradingAccounts,
    clearSubmitTradingAccountsStatus,
    sendOTP,
    clearSendOTPStatus,
    verifyOTP,
    clearVerifyOTPStatus,
    fetchUser,
    clearUserStatus,
    socialSignIn,
    clearSocialSignInStatus,
    socialSignup,
    clearSocialSignupStatus,
    setSignupDetails,
    emailVerification,
    clearEmailVerificationStatus,
    phoneVerification,
    clearPhoneVerificationStatus,
    fetchAccountTypes,
    clearAccountTypesStatus,
    fetchPlatformServers,
    clearPlatformServersStatus,
    initiateForgotPassword,
    clearInitiateForgotPasswordStatus,
    forgotPasswordReset,
    clearForgotPasswordResetStatus,
    updateLeverage,
    clearUpdateLeverageStatus,
    initiateResetPassword,
    clearInitiateResetPasswordStatus,
    resetPassword,
    clearResetPasswordStatus,
    fetchIntroducingBrokerApplication,
    clearIntroducingBrokerApplicationStatus,
    submitIntroducingBrokerApplication,
    clearSubmitIntroducingBrokerApplicationStatus,
    fetchReferralLinks,
    clearReferralLinksStatus,
    submitReferralLink,
    clearSubmitReferralLinkStatus,
    fetchNews,
    clearNewsStatus,
    fetchClientCabinetAccountCommission,
    clearClientCabinetAccountCommissionStatus,
    fetchClientCabinetClientCommission,
    clearClientCabinetClientCommissionStatus,
    fetchClientCabinetCommissionSettings,
    clearClientCabinetCommissionSettingsStatus,
    fetchClientCabinetIBCommissions,
    clearClientCabinetIBCommissionsStatus,
    fetchClientCabinetIBPerformanceDashboard,
    clearClientCabinetIBPerformanceDashboardStatus,
    fetchClientCabinetIBReferrals,
    clearClientCabinetIBReferralsStatus,
    fetchClientCabinetIBTransactions,
    clearClientCabinetIBTransactionsStatus,
    monitorClients,
    clearMonitorClientsStatus,
    monitorMetaClients,
    clearMonitorMetaClientsStatus,
    transfers,
    clearTransfersStatus,
    fetchSocialTradingProviders,
    clearSocialTradingProvidersStatus,
    fetchSocialTradingProviderOffers,
    clearSocialTradingProviderOffersStatus,
    subscribeToSocialTradingProvider,
    clearSubscribeToSocialTradingProviderStatus,
    submitSocialTradingProvider,
    clearSubmitSocialTradingProviderStatus,
    submitSocialTradingProviderOffer,
    clearSubmitSocialTradingProviderOfferStatus,
    fetchSocialTradingProvidersConfig,
    clearSocialTradingProviderConfigStatus,
    fetchSocialTradingProviderPositions,
    clearSocialTradingProviderPositions,
    fetchSocialTradingProviderDetails,
    clearSocialTradingProviderDetailsStatus,
    fetchSocialTradingHistoryDailyChart,
    clearSocialTradingHistoryChartStatus,

    fetchSocialTradingProviderMetrics,
    clearSocialTradingProviderMetricsStatus,
    fetchSocialTradingProviderSubscriptions,
    clearSocialTradingProviderSubscriptionsStatus,
    fetchSocialTradingProviderDeals,
    clearSocialTradingProviderDealsStatus,
    updateSocialTradingProvider,
    clearUpdateSocialTradingProviderStatus,
    fetchSocialTradingProviderPortfolio,
    clearSocialTradingProviderPortfolioStatus,
    updateSocialTradingSubscription,
    clearUpdateSocialTradingSubscriptionStatus,
    fetchSocialTradingSubscriptionDetails,
    clearSocialTradingSubscriptionDetailsStatus,
    fetchSocialTradingSubscriptionDeals,
    clearSocialTradingSubscriptionDealsStatus,
    fetchSocialTradingSubscriptionMetrics,
    clearSocialTradingSubscriptionMetricsStatus,
    fetchSocialTradingSubscriptionTradingResults,
    clearSocialTradingSubscriptionTradingResultsStatus,
    fetchSocialTradingSubscriptionRisk,
    clearSocialTradingSubscriptionRiskStatus,
    updateSocialTradingSubscriptionRisk,
    clearUpdateSocialTradingSubscriptionRiskStatus,
    deleteSocialTradingSubscription,
    clearDeleteSocialTradingSubscriptionStatus,
    fetchSocialTradingFollowerPortfolio,
    clearSocialTradingFollowerPortfolioStatus,
    fetchSocialTradingFollowerPositions,
    clearSocialTradingFollowerPositionsStatus,
    fetchSocialTradingOfferDetails,
    clearSocialTradingOfferDetailsStatus,
    fetchSocialTradingFees,
    clearSocialTradingFeesStatus,
    submitProviderApplication,
    clearSubmitProviderApplicationStatus,
    fetchSocialTradingRateProfile,
    clearSocialTradingRateProfileStatus,
    fetchSocialTradingHistoryIndicators,
    clearSocialTradingHistoryIndicatorsStatus,
    fetchSocialTradingInstrumentReport,
    clearSocialTradingInstrumentReportStatus,
    fetchSocialTradingTradeReport,
    clearSocialTradingTradeReportStatus,
    fetchContacts,
    clearContactsStatus,
    submitContact,
    clearSubmitContactStatus,
    fetchSwapFreeDetails,
    clearSwapFreeDetailsStatus,
    submitSwapFree,
    clearSubmitSwapFreeStatus,
    hydrateUserFromToken,
  };
};
